/**
 * A minimal, dependency-free concurrency limiter. Returns a `run` function that
 * caps how many wrapped async tasks may be in-flight at once across every caller
 * that shares the same limiter instance.
 *
 * Unlike `p-map`, which only bounds concurrency within a single array map, a
 * shared limiter instance enforces a global ceiling regardless of how many
 * independent callers (e.g. React hook instances) are running at once.
 *
 * This is used to bound the medical-results `/history` fan-out: the Lab Results
 * view eagerly fires one request per biomarker across many sections at the same
 * time, which could drain the shared per-account fhir-search-service rate limit
 * and surface as 500s for unrelated requests.
 */
export type ConcurrencyLimiter = <T>(task: () => Promise<T>) => Promise<T>;

export const createConcurrencyLimiter = (limit: number): ConcurrencyLimiter => {
  if (!Number.isInteger(limit) || limit < 1) {
    throw new Error('Concurrency limit must be a positive integer');
  }

  let active = 0;
  const queue: Array<() => void> = [];

  const next = () => {
    if (active >= limit) {
      return;
    }
    const start = queue.shift();
    if (start) {
      start();
    }
  };

  return <T>(task: () => Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const run = () => {
        active += 1;
        // `Promise.resolve().then(task)` ensures a task that throws
        // synchronously is still funneled through the rejection path so the
        // active count is always released.
        Promise.resolve()
          .then(task)
          .then(
            value => {
              active -= 1;
              next();
              resolve(value);
            },
            error => {
              active -= 1;
              next();
              reject(error);
            },
          );
      };

      queue.push(run);
      next();
    });
};
